import { loginRequest } from '@/authConfig';
import Layout from './Layout';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { eventEmitter } from '@/utils/eventEmmiter/eventEmmiter';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { useAuth } from '@/utils/hooks/useAuth';
import { captureAppException, captureAppMessage } from '@/utils/sentry/sentryLogger';

export const PageLayout = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const { logout } = useAuth();
  const [authError, setAuthError] = useState<string | null>(null);

  const AUTH_ERROR_PARAM = 'error=server_error';

  const acquireAccessToken = () => {
    if (!accounts || accounts.length === 0) {
      const allAccounts = instance.getAllAccounts();
      if (allAccounts.length > 0) {
        instance.setActiveAccount(allAccounts[0]);
        const retryRequest = { ...loginRequest, account: allAccounts[0] };
        return instance
          .acquireTokenSilent(retryRequest)
          .then((accessTokenResponse) => {
            localStorage.setItem('ACCESS_TOKEN', accessTokenResponse.accessToken);
          })
          .catch((retryError) => {
            if (retryError instanceof InteractionRequiredAuthError) {
              instance.acquireTokenRedirect(retryRequest);
            } else {
              setAuthError('There was a problem processing your request. Please try again.');
            }
          });
      } else {
        captureAppMessage('MSAL: no accounts available on token acquisition', 'warning', {
          flow: 'auth',
        });
        logout('redirect');
        return Promise.reject(new Error('No accounts available'));
      }
    }

    const accessTokenRequest = { ...loginRequest, account: accounts[0] };

    return instance
      .acquireTokenSilent(accessTokenRequest)
      .then((accessTokenResponse) => {
        localStorage.setItem('ACCESS_TOKEN', accessTokenResponse.accessToken);
      })
      .catch((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(accessTokenRequest);
        } else {
          captureAppException(error, { flow: 'auth', step: 'acquireTokenSilent' });
          setAuthError('There was a problem processing your request. Please try again.');
        }
      });
  };

  const handleTokenExpired = () => {
    acquireAccessToken().catch((error) => {
      if (error.message !== 'No accounts available') {
        localStorage.removeItem('ACCESS_TOKEN');
        localStorage.removeItem('TENANT_ID');
        localStorage.removeItem('FARM_ID');
        setAuthError('Session expired. Please log in again.');
      }
    });
  };

  useEffect(() => {
    eventEmitter.on('TOKEN_EXPIRED', handleTokenExpired);

    return () => {
      eventEmitter.off('TOKEN_EXPIRED', handleTokenExpired);
    };
  }, []);

  useEffect(() => {
    const CURRENT_URL = window.location.href;

    if (CURRENT_URL.includes(AUTH_ERROR_PARAM)) {
      setAuthError(
        'We are unable to verify your account at this time. If the problem persists, please contact support for assistance, support@growdata.com.au'
      );
    } else if (accounts && accounts.length > 0 && inProgress === InteractionStatus.None) {
      acquireAccessToken().catch((error) => {
        console.error('Initial token acquisition failed:', error);
      });
    }
  }, [accounts, instance, inProgress]);

  const handleLogin = async (loginType) => {
    if (loginType === 'redirect') {
      try {
        await instance.loginRedirect(loginRequest);
      } catch (error) {
        captureAppException(error, { flow: 'auth', step: 'loginRedirect' });
        console.error(`LoginRedirect failed: ${error}`);
      }
    }
  };

  return (
    <>
      <AuthenticatedTemplate>
        <Layout>{children}</Layout>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <div className="flex h-screen w-screen items-center justify-center">
          <div className="flex flex-col items-center gap-[110px]">
            <img
              className="w-[500px] rounded"
              src="/logos/growdata-logo-ops-product-dark-green-onwhite.png"
              alt="Growdata logo"
            />

            {authError && (
              <p className="p-2 text-red-500" data-cy="home-error-message">
                {authError}
              </p>
            )}

            <div className="flex w-full justify-center">
              <button
                id="signInButton"
                className="rounded bg-primary px-4 py-2 font-bold text-white hover:bg-primary-light"
                onClick={() => handleLogin('redirect')}
                key="loginRedirect"
              >
                Sign in
              </button>
            </div>
          </div>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
};
